import { useEffect, useRef, useState, useCallback } from "react";
import { useBroadcastState } from "@/components/BroadcastGraphics";
import { TvFrame, TvTicker } from "@/components/broadcast/TvFrame";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { StudioRundown, RundownChips, loadRundown, RUNDOWN_KEY } from "@/components/admin/StudioRundown";
import { ClipTrimmer } from "@/components/admin/ClipTrimmer";
import { Camera, CameraOff, Circle, Square, ChevronLeft, ChevronRight, Upload, Trash2, Loader2, Play, Pause, Clock, ExternalLink, AlertTriangle, Download, Scissors } from "lucide-react";

const CORNERS = { br: "bottom-6 right-6", bl: "bottom-6 left-6", tr: "top-6 right-6", tl: "top-6 left-6" };
const CAM_W = 460, CAM_H = 345, CAM_PAD = 24;
const EMBEDDED = (() => { try { return window.self !== window.top; } catch { return true; } })();
const REGION_CAPTURE = typeof window !== "undefined" && "CropTarget" in window;

/** Draw the camera feed as a rounded, bordered box onto the recording canvas (same spot as the on-stage box). */
function drawCamBox(ctx, cam, corner, cw, ch) {
  if (!cam || !cam.videoWidth) return;
  const k = cw / 1920;
  const bw = CAM_W * k, bh = CAM_H * k, pad = CAM_PAD * k, bd = 4 * k, r = 12 * k;
  const x = corner.endsWith("r") ? cw - pad - bw : pad;
  const y = corner.startsWith("b") ? ch - pad - bh : pad;
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(x, y, bw, bh, r);
  ctx.clip();
  const sc = Math.max(bw / cam.videoWidth, bh / cam.videoHeight);
  const dw = cam.videoWidth * sc, dh = cam.videoHeight * sc;
  ctx.drawImage(cam, x + (bw - dw) / 2, y + (bh - dh) / 2, dw, dh);
  ctx.restore();
  ctx.lineWidth = bd;
  ctx.strokeStyle = "#fff";
  ctx.beginPath();
  ctx.roundRect(x + bd / 2, y + bd / 2, bw - bd, bh - bd, r);
  ctx.stroke();
}
const EXPIRY_OPTS = [[0, "Never"], [6, "6 hours"], [12, "12 hours"], [24, "24 hours"], [48, "2 days"], [72, "3 days"]];
const SOFT_LIMIT_S = 300, HARD_LIMIT_S = 600; // recommended / too-long thresholds for a loop clip
const AUTO_STOP_OPTS = [[0, "Off"], [180, "3:00"], [300, "5:00"], [420, "7:00"], [600, "10:00"]];
const lengthTone = (s) => (s >= HARD_LIMIT_S ? "red" : s >= SOFT_LIMIT_S ? "amber" : "ok");

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.round(s) % 60).padStart(2, "0")}`;
function hoursLeft(exp) {
  if (!exp) return null;
  const ms = new Date(exp).getTime() - Date.now();
  return ms <= 0 ? 0 : ms / 3600000;
}
function expiryLabel(exp) {
  const h = hoursLeft(exp);
  if (h == null) return null;
  if (h === 0) return "Expired";
  if (h < 1) return `Expires in ${Math.round(h * 60)}m`;
  if (h < 48) return `Expires in ${Math.round(h)}h`;
  return `Expires in ${Math.round(h / 24)}d`;
}

export function ForecastStudio() {
  const { allSlides, tickerFeed, cities } = useBroadcastState({ manual: true });
  const stageRef = useRef(null);
  const wrapRef = useRef(null);
  const camRef = useRef(null);
  const dispVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const camStream = useRef(null);
  const displayStream = useRef(null);
  const recRef = useRef(null);
  const chunks = useRef([]);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const promptBoxRef = useRef(null);
  const monitorRef = useRef(null);
  const stallRef = useRef({ t: 0, at: 0 });
  const [stalled, setStalled] = useState(false);
  const croppedRef = useRef(false);
  const cornerRef = useRef("br");

  const [scale, setScale] = useState(0.3);
  const [camOn, setCamOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recLayout, setRecLayout] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef(0);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [corner, setCorner] = useState("br");
  useEffect(() => { cornerRef.current = corner; }, [corner]);
  const [lib, setLib] = useState({ enabled: true, max_count: 5, default_expiry_hours: null, videos: [] });
  const [expiryHours, setExpiryHours] = useState(0);
  const [autoStop, setAutoStop] = useState(() => Number(localStorage.getItem("gawx_autostop") || 0));
  useEffect(() => { localStorage.setItem("gawx_autostop", String(autoStop)); }, [autoStop]);
  const autoStopRef = useRef(0);
  useEffect(() => { autoStopRef.current = autoStop; }, [autoStop]);

  const [notes, setNotes] = useState(() => localStorage.getItem("gawx_prompter") || "");
  const [promptPlay, setPromptPlay] = useState(true);
  const [promptSpeed, setPromptSpeed] = useState(2);

  // Which graphic is on the stage + the user's click-through rundown (persisted)
  const [curId, setCurId] = useState(null);
  const [rundown, setRundown] = useState(loadRundown);
  useEffect(() => { localStorage.setItem(RUNDOWN_KEY, JSON.stringify(rundown)); }, [rundown]);
  const seq = rundown.filter((id) => allSlides.some((s) => s.id === id));
  const order = seq.length ? seq : allSlides.map((s) => s.id);
  const current = allSlides.find((s) => s.id === curId) || allSlides.find((s) => s.id === order[0]) || allSlides[0];
  const stepPos = seq.indexOf(current?.id);
  const stepLabel = stepPos >= 0 ? `${stepPos + 1}/${seq.length} · ` : "";
  const atlanta = (cities || []).find((c) => c.id === "atlanta") || (cities || [])[0];
  const tickerUrgent = tickerFeed?.some((i) => i.urgent);

  const loadLib = useCallback(() => {
    api.get("/forecast-videos?include_expired=1").then((r) => {
      setLib(r.data || { videos: [] });
      setExpiryHours((h) => (h ? h : (r.data?.default_expiry_hours || 0)));
    }).catch(() => {});
  }, []);
  useEffect(() => { loadLib(); }, [loadLib]);
  useEffect(() => { localStorage.setItem("gawx_prompter", notes); }, [notes]);

  // Scale the fixed 1920x1080 stage to its wrapper (bigger while recording)
  useEffect(() => {
    const fit = () => {
      if (recLayout) {
        setScale(Math.min((window.innerWidth * 0.72) / 1920, (window.innerHeight * 0.6) / 1080));
      } else {
        const w = wrapRef.current?.clientWidth || 960;
        setScale(w / 1920);
      }
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [recLayout]);

  const move = useCallback((d) => {
    if (!order.length) return;
    const i = order.indexOf(current?.id);
    setCurId(order[(i + d + order.length) % order.length]);
  }, [order, current]);
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target?.tagName)) return;
      if (e.key === "ArrowRight") move(1);
      else if (e.key === "ArrowLeft") move(-1);
      else if (/^[1-9]$/.test(e.key) && seq[Number(e.key) - 1]) setCurId(seq[Number(e.key) - 1]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move, seq]);

  // Teleprompter auto-scroll while recording
  useEffect(() => {
    if (!recLayout || !promptPlay) return undefined;
    const t = setInterval(() => { if (promptBoxRef.current) promptBoxRef.current.scrollTop += promptSpeed; }, 40);
    return () => clearInterval(t);
  }, [recLayout, promptPlay, promptSpeed]);

  // The stage re-mounts when the recording layout opens, so re-attach the camera stream whenever the <video> mounts
  const attachCam = useCallback((el) => {
    camRef.current = el;
    if (el && camStream.current && el.srcObject !== camStream.current) {
      el.srcObject = camStream.current;
      el.play().catch(() => {});
    }
  }, []);

  const CAM_ERRORS = {
    NotAllowedError: "Camera/mic permission is blocked. Click the camera icon in Chrome's address bar, allow Camera and Microphone, then reload.",
    NotReadableError: "Your camera is in use by another tab or app (Zoom, Teams, OBS, or the other Studio tab). Close it and try again.",
    NotFoundError: "No camera or microphone was found. Plug one in or check your system settings.",
    OverconstrainedError: "Your camera doesn't support 720p — retrying at default quality.",
  };
  const startCam = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { toast.error("This browser can't access the camera here — use Chrome over HTTPS."); return; }
    try {
      let s;
      try {
        s = await navigator.mediaDevices.getUserMedia({ video: { width: 1280, height: 720 }, audio: true });
      } catch (e) {
        if (e?.name !== "OverconstrainedError") throw e;
        s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      }
      camStream.current = s;
      if (camRef.current) camRef.current.srcObject = s;
      setCamOn(true);
    } catch (e) {
      toast.error(CAM_ERRORS[e?.name] || `Could not access camera/mic (${e?.name || "unknown error"}).`, { duration: 9000 });
    }
  };

  // Opened via "Open Studio in its own tab" → start the camera right away so Record is ready
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("autocam") === "1" && !camStream.current) {
      const t = setTimeout(() => { startCam(); toast.info("Studio opened in its own tab — starting your camera. Next: hit Record and pick \"This tab\"."); }, 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []); // eslint-disable-line
  const stopCam = () => {
    camStream.current?.getTracks().forEach((t) => t.stop());
    camStream.current = null; setCamOn(false);
  };

  const cleanupStreams = () => {
    cancelAnimationFrame(rafRef.current);
    displayStream.current?.getTracks().forEach((t) => t.stop());
    displayStream.current = null;
    if (dispVideoRef.current) dispVideoRef.current.srcObject = null;
    clearInterval(timerRef.current);
    setRecLayout(false);
    setCountdown(null);
  };

  const drawLoop = () => {
    const dv = dispVideoRef.current, cv = canvasRef.current, st = stageRef.current;
    if (dv && cv && st && dv.videoWidth) {
      const ctx = cv.getContext("2d");
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, cv.width, cv.height);
      try {
        if (croppedRef.current) {
          ctx.drawImage(dv, 0, 0, cv.width, cv.height); // Region Capture already cropped the track to the stage
        } else {
          const rect = st.getBoundingClientRect();
          const de = document.documentElement; // tab capture = viewport without scrollbars
          const sx = dv.videoWidth / de.clientWidth;
          const sy = dv.videoHeight / de.clientHeight;
          ctx.drawImage(dv, rect.left * sx, rect.top * sy, rect.width * sx, rect.height * sy, 0, 0, cv.width, cv.height);
        }
      } catch { /* frame not ready */ }
      // Camera is composited here, so it is in the recording no matter how the tab was captured
      drawCamBox(ctx, camRef.current, cornerRef.current, cv.width, cv.height);
      // Stall detector: if the captured tab video stops advancing for 3s the recording is freezing
      const st_ = stallRef.current;
      if (dv.currentTime !== st_.t) { st_.t = dv.currentTime; st_.at = performance.now(); setStalled(false); }
      else if (st_.at && performance.now() - st_.at > 3000) setStalled(true);
      const mon = monitorRef.current;
      if (mon) mon.getContext("2d").drawImage(cv, 0, 0, mon.width, mon.height);
    }
    rafRef.current = requestAnimationFrame(drawLoop);
  };

  const startRec = async () => {
    if (!camStream.current || !camStream.current.active) { toast.error("Start your camera first."); return; }
    if (recRef.current && recRef.current.state === "recording") return;
    if (displayStream.current) cleanupStreams();
    if (EMBEDDED && !REGION_CAPTURE) {
      toast.error("Recording inside the preview frame isn't supported in this browser — open the Studio in its own tab.");
      return;
    }
    let display;
    try {
      display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30, displaySurface: "browser", cursor: "never" }, audio: false,
        preferCurrentTab: true, selfBrowserSurface: "include", surfaceSwitching: "exclude", monitorTypeSurfaces: "exclude",
      });
    } catch (e) {
      const why = e?.name === "NotAllowedError" ? "Screen share was cancelled or blocked." : `Screen share failed (${e?.name || "unknown"}: ${e?.message || ""}).`;
      toast.error(why, { duration: 8000 });
      return;
    }
    displayStream.current = display;
    setPreview(null);
    setRecLayout(true);
    if (dispVideoRef.current) { dispVideoRef.current.srcObject = display; await dispVideoRef.current.play().catch(() => {}); }
    if (promptBoxRef.current) promptBoxRef.current.scrollTop = 0;
    await sleep(250); // let the recording layout mount — the crop target must be the stage that stays on screen while recording

    // Region Capture: lock the shared tab to the recording-layout stage (that element lives for the whole recording;
    // locking to the normal-layout stage froze/blacked out the capture when it unmounted)
    croppedRef.current = false;
    let cropErr = "";
    if (REGION_CAPTURE && stageRef.current) {
      try {
        const target = await window.CropTarget.fromElement(stageRef.current);
        await display.getVideoTracks()[0].cropTo(target);
        croppedRef.current = true;
      } catch (e) {
        cropErr = `${e?.name || "error"}: ${e?.message || ""}`;
      }
    }
    if (EMBEDDED && !croppedRef.current) {
      cleanupStreams();
      toast.error(`Couldn't lock the recording to the TV stage from inside the preview (${cropErr}) — open the Studio in its own tab and record there.`, { duration: 10000 });
      return;
    }

    // 3-2-1 countdown (recorder not yet running, so it is never captured)
    for (let n = 3; n >= 1; n--) { setCountdown(n); await sleep(850); }
    setCountdown(null);

    if (!displayStream.current) return; // cancelled during countdown
    display.getVideoTracks()[0].onended = () => { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); };

    rafRef.current = requestAnimationFrame(drawLoop);
    const canvasStream = canvasRef.current.captureStream(30);
    const mixed = new MediaStream([...canvasStream.getVideoTracks(), ...camStream.current.getAudioTracks()]);
    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm";
    const rec = new MediaRecorder(mixed, { mimeType: mime, videoBitsPerSecond: 2500000 });
    chunks.current = [];
    rec.ondataavailable = (e) => e.data && e.data.size && chunks.current.push(e.data);
    rec.onstop = () => {
      const blob = new Blob(chunks.current, { type: "video/webm" });
      setPreview({ url: URL.createObjectURL(blob), blob, dur: elapsedRef.current });
      cleanupStreams();
      setRecording(false);
    };
    recRef.current = rec;
    setElapsed(0);
    elapsedRef.current = 0;
    stallRef.current = { t: 0, at: 0 };
    setStalled(false);
    rec.start(1000);
    setRecording(true);
    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
      if (autoStopRef.current && elapsedRef.current >= autoStopRef.current && recRef.current?.state === "recording") {
        toast.info(`Auto-stopped at ${mmss(autoStopRef.current)} — review your clip below.`);
        recRef.current.stop();
      }
    }, 1000);
  };

  const stopRec = () => { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); };
  const cancelCountdown = () => { cleanupStreams(); };

  useEffect(() => () => { cleanupStreams(); stopCam(); }, []); // eslint-disable-line

  const save = async () => {
    if (!preview?.blob) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", preview.blob, "forecast.webm");
      fd.append("title", title || "Forecast Update");
      fd.append("duration_s", String(preview.dur || 0));
      fd.append("expires_hours", String(expiryHours || 0));
      await api.post("/forecast-videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const lbl = EXPIRY_OPTS.find((o) => o[0] === Number(expiryHours))?.[1];
      toast.success(expiryHours ? `Saved — airs for ${lbl}.` : "Saved — it's now in the 24/7 loop.");
      setPreview(null); setTitle("");
      loadLib();
    } catch { toast.error("Upload failed. Try a shorter recording."); }
    finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/forecast-videos/${id}`).catch(() => {}); loadLib(); };
  const setExpiry = async (id, hours) => { await api.put(`/forecast-videos/${id}/expiry`, { hours: Number(hours) }).catch(() => {}); loadLib(); };
  const saveCfg = async (patch) => { const r = await api.put("/forecast-videos/config", patch).catch(() => null); if (r) setLib((l) => ({ ...l, ...r.data })); };

  const [dling, setDling] = useState(null);
  const [trimClip, setTrimClip] = useState(null);
  const saveBlobAs = (blob, name) => {
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };
  const safeName = (s) => (s || "forecast").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "forecast";
  const downloadClip = async (v) => {
    setDling(v.id);
    try {
      const resp = await fetch(`${BACKEND_URL}${v.url}`);
      if (!resp.ok) throw new Error("fetch failed");
      const blob = await resp.blob();
      const ext = (blob.type || "").includes("mp4") ? "mp4" : "webm";
      saveBlobAs(blob, `${safeName(v.title)}.${ext}`);
    } catch { toast.error("Download failed — try again."); }
    finally { setDling(null); }
  };

  const StageBlock = (
    <div ref={stageRef} className="absolute left-1/2 top-1/2 bg-black" style={{ width: 1920, height: 1080, transform: `translate(-50%,-50%) scale(${scale})`, transformOrigin: "center center" }}>
      {current && (
        <TvFrame title={current.title} right={current.right} bugTemp={atlanta?.current_temp} fullBleed={current.fullBleed} chromeless={current.chromeless} hasTicker>
          {current.node}
        </TvFrame>
      )}
      <TvTicker label={tickerUrgent ? "Alert" : "GAWX Live"} items={tickerFeed || []} urgent={tickerUrgent} />
      {camOn && (
        <div className={`absolute ${CORNERS[corner]} z-50 overflow-hidden rounded-xl border-4 border-white shadow-2xl ${recLayout ? "opacity-0" : ""}`} style={{ width: CAM_W, height: CAM_H }} data-testid="studio-cam-box">
          <video ref={attachCam} autoPlay playsInline muted className="h-full w-full object-cover" style={{ transform: "scaleX(-1)" }} />
        </div>
      )}
      {countdown != null && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/55">
          <div className="font-oswald text-[320px] font-black leading-none text-white drop-shadow-[0_10px_40px_rgba(0,0,0,0.6)]" data-testid="studio-countdown">{countdown}</div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="forecast-studio">
      {/* Kept rendered (off-screen, not display:none) — Chrome stops delivering frames to hidden capture videos, which froze recordings */}
      <video ref={dispVideoRef} muted playsInline autoPlay className="pointer-events-none fixed left-0 top-0 h-[2px] w-[2px] opacity-0" aria-hidden="true" />
      <canvas ref={canvasRef} width={1280} height={720} className="hidden" />

      <div>
        <h2 className="font-oswald text-2xl font-bold uppercase tracking-wide text-white">Forecast Studio</h2>
        <p className="mt-1 text-sm text-gray-400">Record yourself over the live graphics — no OBS. Start your camera, pick the graphics you want and build a rundown to click through (the stage never auto-advances here), write your script in the teleprompter, then hit record. You'll get a <b>"Share this tab"</b> prompt (pick <b>this tab</b>), a 3-2-1 countdown, and your clip auto-joins the 24/7 loop. Only the TV stage is recorded — your camera is composited straight into the video and the teleprompter stays off-camera.</p>
      </div>

      {EMBEDDED && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#ffd60a]/40 bg-[#ffd60a]/10 px-4 py-3" data-testid="studio-embedded-notice">
          <AlertTriangle className="h-5 w-5 shrink-0 text-[#ffd60a]" />
          <div className="min-w-0 flex-1 text-sm text-gray-200">
            You're viewing the Studio inside another page (the preview frame). {REGION_CAPTURE ? "Recording will lock onto the TV stage automatically, but for the most reliable capture" : "Recording from here isn't supported in this browser —"} open the Studio in its own browser tab and record there.
          </div>
          <button onClick={() => { stopCam(); window.open(`${window.location.origin}/admin?tab=studio&autocam=1`, "_blank", "noopener"); }} data-testid="studio-open-tab" className="flex items-center gap-2 rounded-md bg-[#ffd60a] px-4 py-2 font-oswald text-sm font-bold uppercase tracking-wider text-[#0a0f1c] hover:bg-[#ffe34a]"><ExternalLink className="h-4 w-4" /> Open Studio in its own tab</button>
        </div>
      )}

      {/* Recording overlay: stage is cropped into the video; prompter + controls stay off-camera */}
      {recLayout && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-black" data-testid="studio-rec-overlay">
          <div className="relative flex-1">{StageBlock}</div>
          <div className="relative h-[34vh] shrink-0 border-t border-white/10 bg-[#070a12] px-8 py-4">
            <div className="mb-2 flex flex-wrap items-center gap-3">
              {recording ? (
                <>
                  <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 font-oswald text-[18px] font-black uppercase tracking-widest ${lengthTone(elapsed) === "red" ? "bg-white text-[#1f63c9]" : lengthTone(elapsed) === "amber" ? "bg-[#ffd60a] text-[#0a0f1c]" : "bg-[#1f63c9] text-white"}`} data-testid="studio-rec-badge">
                    <span className={`h-3 w-3 animate-pulse rounded-full ${lengthTone(elapsed) === "ok" ? "bg-white" : "bg-[#1f63c9]"}`} /> REC {mmss(elapsed)}
                  </div>
                <button onClick={stopRec} data-testid="studio-stop" className="flex items-center gap-2 rounded-md bg-white px-5 py-2 font-oswald text-sm font-bold uppercase tracking-wider text-[#0a0f1c] hover:bg-gray-200"><Square className="h-4 w-4 fill-[#0a0f1c]" /> Stop &amp; Review</button>
                </>
              ) : (
                <button onClick={cancelCountdown} className="rounded-md bg-white/10 px-4 py-2 font-oswald text-sm font-bold uppercase tracking-wider text-white hover:bg-white/20">Cancel</button>
              )}
              <button onClick={() => move(-1)} className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10 text-white hover:bg-white/20"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => move(1)} className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10 text-white hover:bg-white/20"><ChevronRight className="h-4 w-4" /></button>
              <span className="font-oswald text-xs uppercase tracking-wider text-gray-500" data-testid="studio-rec-current">{stepLabel}{current?.title}</span>
              <div className="mx-2 h-6 w-px bg-white/15" />
              <div className="flex items-center gap-2" data-testid="studio-length-meter">
                <div className="relative h-2 w-40 overflow-hidden rounded-full bg-white/10">
                  <div className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-500" style={{ width: `${Math.min(100, (elapsed / HARD_LIMIT_S) * 100)}%`, background: lengthTone(elapsed) === "red" ? "#1f63c9" : lengthTone(elapsed) === "amber" ? "#ffd60a" : "#1f63c9" }} />
                  <div className="absolute inset-y-0 w-px bg-white/50" style={{ left: `${(SOFT_LIMIT_S / HARD_LIMIT_S) * 100}%` }} />
                </div>
                <span className={`font-oswald text-xs font-bold uppercase tracking-wider ${lengthTone(elapsed) === "red" ? "text-[#2a74d8]" : lengthTone(elapsed) === "amber" ? "text-[#ffd60a]" : "text-gray-400"}`} data-testid="studio-length-hint">
                  {lengthTone(elapsed) === "red" ? "Too long for the loop — wrap it up" : lengthTone(elapsed) === "amber" ? "Over 5:00 — consider wrapping up" : "Recommended: under 5:00"}
                </span>
                {autoStop > 0 && <span className="font-oswald text-[11px] uppercase tracking-wider text-gray-500">· auto-stop {mmss(autoStop)}</span>}
              </div>
              <div className="mx-2 h-6 w-px bg-white/15" />
              <span className="font-oswald text-xs uppercase tracking-wider text-gray-400">Prompter</span>
              <button onClick={() => setPromptPlay((p) => !p)} className="flex h-8 w-8 items-center justify-center rounded-md bg-white/10 text-white hover:bg-white/20">{promptPlay ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</button>
              <input type="range" min="1" max="8" value={promptSpeed} onChange={(e) => setPromptSpeed(Number(e.target.value))} className="w-28" />
              {stalled && <span className="flex items-center gap-1 rounded bg-[#1f63c9] px-2 py-1 font-oswald text-xs font-bold uppercase tracking-widest text-white" data-testid="studio-stalled"><AlertTriangle className="h-3.5 w-3.5" /> Capture stalled — stop, then record again</span>}
              <span className="ml-auto font-oswald text-xs uppercase tracking-widest text-gray-500">Only the stage above is recorded</span>
            </div>
            <div className="absolute right-8 top-16 z-10 overflow-hidden rounded-md border border-white/20 bg-black shadow-xl" data-testid="studio-monitor">
              <canvas ref={monitorRef} width={320} height={180} className="block" />
              <div className="absolute left-1.5 top-1.5 rounded bg-black/70 px-1.5 py-0.5 font-oswald text-[10px] font-bold uppercase tracking-widest text-[#ffd60a]">Recording monitor</div>
            </div>
            <div className="mb-2"><RundownChips allSlides={allSlides} seq={seq} currentId={current?.id} onShow={setCurId} /></div>
            <div ref={promptBoxRef} className={`${seq.length ? "h-[calc(34vh-104px)]" : "h-[calc(34vh-62px)]"} overflow-hidden rounded-lg bg-black/60 px-10 py-6`} data-testid="studio-prompter">
              <div className="mx-auto max-w-4xl whitespace-pre-wrap text-center font-oswald text-[34px] font-bold leading-snug text-white">{notes || "Write your script in the Teleprompter box before recording."}</div>
              <div className="h-[30vh]" />
            </div>
          </div>
        </div>
      )}

      {/* Normal stage preview */}
      <div ref={wrapRef} className="relative w-full overflow-hidden rounded-lg border border-white/10 bg-black" style={{ aspectRatio: "16 / 9" }}>
        {!recLayout && StageBlock}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        {!camOn ? (
          <button onClick={startCam} data-testid="studio-start-cam" className="flex items-center gap-2 rounded-md bg-white/10 px-4 py-2 font-oswald text-sm font-bold uppercase tracking-wider text-white hover:bg-white/20"><Camera className="h-4 w-4" /> Start Camera</button>
        ) : (
          <button onClick={stopCam} disabled={recording} className="flex items-center gap-2 rounded-md bg-white/10 px-4 py-2 font-oswald text-sm font-bold uppercase tracking-wider text-white hover:bg-white/20 disabled:opacity-40"><CameraOff className="h-4 w-4" /> Stop Camera</button>
        )}
        <button onClick={startRec} disabled={!camOn || recLayout} data-testid="studio-record" title={camOn ? "Record the stage + your camera" : "Start your camera first"} className="flex items-center gap-2 rounded-md bg-[#1f63c9] px-5 py-2 font-oswald text-sm font-bold uppercase tracking-wider text-white hover:bg-[#2a74d8] disabled:opacity-40"><Circle className="h-4 w-4 fill-white" /> Record</button>
        {!camOn && <span className="font-oswald text-[11px] uppercase tracking-wider text-[#ffd60a]" data-testid="studio-record-hint">← Start your camera first</span>}
        <div className="mx-2 h-6 w-px bg-white/15" />
        <button onClick={() => move(-1)} className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10 text-white hover:bg-white/20"><ChevronLeft className="h-4 w-4" /></button>
        <span className="min-w-[200px] font-oswald text-sm font-bold uppercase tracking-wider text-gray-300" data-testid="studio-current">{stepLabel}{current?.title || "—"}</span>
        <button onClick={() => move(1)} className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10 text-white hover:bg-white/20"><ChevronRight className="h-4 w-4" /></button>
        <div className="mx-2 h-6 w-px bg-white/15" />
        <label className="flex items-center gap-2 font-oswald text-xs uppercase tracking-wider text-gray-400"><Clock className="h-4 w-4" /> Auto-stop
          <select value={autoStop} onChange={(e) => setAutoStop(Number(e.target.value))} data-testid="studio-autostop" className="rounded bg-black/40 px-2 py-1 text-white">
            {AUTO_STOP_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <div className="mx-2 h-6 w-px bg-white/15" />
        <span className="font-oswald text-xs uppercase tracking-wider text-gray-400">Cam corner:</span>
        {Object.keys(CORNERS).map((k) => (
          <button key={k} onClick={() => setCorner(k)} className={`rounded px-2 py-1 font-oswald text-xs font-bold uppercase ${corner === k ? "bg-[#1f63c9] text-white" : "bg-white/10 text-gray-300"}`}>{k.toUpperCase()}</button>
        ))}
      </div>

      <StudioRundown allSlides={allSlides} rundown={rundown} setRundown={setRundown} currentId={current?.id} onShow={setCurId} />

      {/* Teleprompter editor */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-4">
        <div className="mb-2 font-oswald text-sm font-bold uppercase tracking-wider text-white">Teleprompter script</div>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="studio-notes" rows={4} placeholder="Type your forecast script here. It scrolls below the stage while you record (viewers never see it)." className="w-full rounded-md border border-white/15 bg-black/40 px-3 py-2 font-oswald text-sm leading-relaxed text-white placeholder:text-gray-500" />
      </div>

      {/* Preview + save */}
      {preview && (
        <div className="rounded-lg border border-white/15 bg-white/5 p-4" data-testid="studio-preview">
          <div className="flex flex-wrap items-center gap-3">
            <div className="font-oswald text-sm font-bold uppercase tracking-wider text-white">Preview ({mmss(preview.dur)})</div>
            <span className={`rounded px-2 py-0.5 font-oswald text-[11px] font-bold uppercase tracking-wider ${lengthTone(preview.dur) === "red" ? "bg-[#1f63c9] text-white" : lengthTone(preview.dur) === "amber" ? "bg-[#ffd60a] text-[#0a0f1c]" : "bg-[#1f63c9]/30 text-[#9cc3ff]"}`} data-testid="studio-preview-verdict">
              {lengthTone(preview.dur) === "red" ? "Long for the loop — consider re-recording shorter or setting Auto-retire" : lengthTone(preview.dur) === "amber" ? "A bit long — fine for a big weather day" : "Great length for the loop"}
            </span>
          </div>
          <video src={preview.url} controls className="mt-3 w-full max-w-2xl rounded-md bg-black" />
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Clip title (e.g. Evening Forecast)" data-testid="studio-title" className="w-72 rounded-md border border-white/15 bg-black/40 px-3 py-2 font-oswald text-sm text-white placeholder:text-gray-500" />
            <label className="flex items-center gap-2 font-oswald text-xs uppercase tracking-wider text-gray-300"><Clock className="h-4 w-4" /> Auto-retire
              <select value={expiryHours} onChange={(e) => setExpiryHours(Number(e.target.value))} data-testid="studio-expiry" className="rounded bg-black/40 px-2 py-1 text-white">
                {EXPIRY_OPTS.map(([h, l]) => <option key={h} value={h}>{l}</option>)}
              </select>
            </label>
            <button onClick={save} disabled={saving} data-testid="studio-save" className="flex items-center gap-2 rounded-md bg-[#1f63c9] px-5 py-2 font-oswald text-sm font-bold uppercase tracking-wider text-white hover:bg-[#2a74d8] disabled:opacity-50">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Save to Stream</button>
            <button onClick={() => saveBlobAs(preview.blob, `${safeName(title || "forecast")}.webm`)} data-testid="studio-download-preview" className="flex items-center gap-2 rounded-md bg-white/10 px-4 py-2 font-oswald text-sm font-bold uppercase tracking-wider text-white hover:bg-white/20"><Download className="h-4 w-4" /> Download</button>
            <button onClick={() => { setPreview(null); setTitle(""); }} className="rounded-md bg-white/10 px-4 py-2 font-oswald text-sm font-bold uppercase tracking-wider text-white hover:bg-white/20">Discard</button>
          </div>
        </div>
      )}

      {/* Library + loop config */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="font-oswald text-sm font-bold uppercase tracking-wider text-white">Clips ({lib.videos?.length || 0})</div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 font-oswald text-xs uppercase tracking-wider text-gray-300"><input type="checkbox" checked={!!lib.enabled} onChange={(e) => saveCfg({ enabled: e.target.checked })} data-testid="studio-enabled" /> Air in stream</label>
            <label className="flex items-center gap-2 font-oswald text-xs uppercase tracking-wider text-gray-300">Keep newest
              <select value={lib.max_count || 5} onChange={(e) => saveCfg({ max_count: Number(e.target.value) })} data-testid="studio-cap" className="rounded bg-black/40 px-2 py-1 text-white">
                {[3, 5, 8, 10, 15].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 font-oswald text-xs uppercase tracking-wider text-gray-300">New clips expire
              <select value={lib.default_expiry_hours || 0} onChange={(e) => saveCfg({ default_expiry_hours: Number(e.target.value) })} data-testid="studio-default-expiry" className="rounded bg-black/40 px-2 py-1 text-white">
                {EXPIRY_OPTS.map(([h, l]) => <option key={h} value={h}>{l}</option>)}
              </select>
            </label>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {(lib.videos || []).map((v) => (
            <div key={v.id} className={`overflow-hidden rounded-md border border-white/10 bg-black ${v.expired ? "opacity-50" : ""}`} data-testid={`studio-clip-${v.id}`}>
              <div className="relative">
                <video src={`${BACKEND_URL}${v.url}`} className="aspect-video w-full bg-black object-contain" preload="metadata" muted />
                {expiryLabel(v.expires_at) && (
                  <span className={`absolute right-1.5 top-1.5 rounded px-1.5 py-0.5 font-oswald text-[10px] font-bold uppercase tracking-wider ${v.expired ? "bg-[#1f63c9] text-white" : "bg-black/70 text-[#ffd60a]"}`}>{expiryLabel(v.expires_at)}</span>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 px-3 py-2">
                <div className="min-w-0">
                  <div className="truncate font-oswald text-sm font-bold uppercase tracking-wide text-white">{v.title}</div>
                  <div className="font-oswald text-[11px] uppercase tracking-widest text-gray-500">{v.duration_s ? mmss(Math.round(v.duration_s)) : ""}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button onClick={() => setTrimClip(v)} title="Re-trim this clip" className="rounded p-1.5 text-gray-400 hover:bg-white/10 hover:text-[#1f63c9]" data-testid={`studio-trim-${v.id}`}><Scissors className="h-4 w-4" /></button>
                  <button onClick={() => downloadClip(v)} disabled={dling === v.id} title="Download this clip" className="rounded p-1.5 text-gray-400 hover:bg-white/10 hover:text-[#ffd60a] disabled:opacity-40" data-testid={`studio-download-${v.id}`}>{dling === v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}</button>
                  <select value="" onChange={(e) => e.target.value !== "" && setExpiry(v.id, e.target.value)} title="Set expiry" className="rounded bg-white/10 px-1 py-1 text-[11px] text-white" data-testid={`studio-clip-expiry-${v.id}`}>
                    <option value="">{v.expired ? "Re-air" : "Expiry"}</option>
                    {EXPIRY_OPTS.map(([h, l]) => <option key={h} value={h}>{l}</option>)}
                  </select>
                  <button onClick={() => del(v.id)} className="rounded p-1.5 text-gray-400 hover:bg-white/10 hover:text-[#2a74d8]" data-testid={`studio-del-${v.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
          {!(lib.videos || []).length && <div className="col-span-full py-6 text-center font-oswald text-sm uppercase tracking-wider text-gray-500">No forecast clips yet — record one above.</div>}
        </div>
      </div>
      {trimClip && <ClipTrimmer clip={trimClip} onClose={() => setTrimClip(null)} onSaved={() => { setTrimClip(null); loadLib(); }} />}
    </div>
  );
}
